/**
 * Enhanced color utility functions for converting between different color formats
 * and performing advanced color analysis for skin tone detection
 */
import {
  seasonFromSkin,
  type SeasonUndertone,
} from './seasonUndertone';

interface RGB {
  r: number;
  g: number;
  b: number;
  hex?: string;
}

interface HSV {
  h: number;
  s: number;
  v: number;
}

interface Lab {
  L: number;
  a: number;
  b: number;
}

interface YUV {
  y: number;
  u: number;
  v: number;
}

interface SkinToneAnalysis {
  rgb: RGB;
  hsv: HSV;
  lab: Lab;
  yuv: YUV;
  isSkinTone: boolean;
  confidence: number;
  skinToneCategory: string;
  undertone: SeasonUndertone;
}

/**
 * Convert hex color to rgba string
 */
export const hexToRgba = (hex: string, alpha: number = 1): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * Convert RGB to hex color string
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  const toHex = (value: number) => {
    const hex = Math.round(value).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
};

/**
 * Convert hex color string to RGB
 */
export const hexToRgb = (hex: string): RGB | null => {
  // Remove # if present
  hex = hex.replace(/^#/, '');

  // Parse the hex string
  let r, g, b;

  if (hex.length === 3) {
    // 3 digits
    r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
    g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
    b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
  } else if (hex.length === 6) {
    // 6 digits
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    return null;
  }

  return { r, g, b, hex: `#${hex}` };
};

/**
 * Convert HSV to RGB
 */
export const HSVtoRGB = (h: number, s: number, v: number): RGB => {
  let r = 0,
    g = 0,
    b = 0;

  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  switch (i % 6) {
    case 0:
      r = v;
      g = t;
      b = p;
      break;
    case 1:
      r = q;
      g = v;
      b = p;
      break;
    case 2:
      r = p;
      g = v;
      b = t;
      break;
    case 3:
      r = p;
      g = q;
      b = v;
      break;
    case 4:
      r = t;
      g = p;
      b = v;
      break;
    case 5:
      r = v;
      g = p;
      b = q;
      break;
  }

  const rgb: RGB = {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };

  rgb.hex = rgbToHex(rgb.r, rgb.g, rgb.b);

  return rgb;
};

/**
 * Convert RGB to HSV
 */
export const RGBtoHSV = (r: number, g: number, b: number): HSV => {
  // Convert RGB to [0, 1] range
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  const s = max === 0 ? 0 : delta / max;
  const v = max;

  if (delta === 0) {
    h = 0;
  } else {
    if (max === r) {
      h = ((g - b) / delta) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }

    h *= 60;
    if (h < 0) h += 360;
  }

  return { h, s, v };
};

/**
 * Convert RGB to Lab color space
 */
export const RGBtoLab = (r: number, g: number, b: number): Lab => {
  // Normalize RGB values
  r = r / 255;
  g = g / 255;
  b = b / 255;

  // Convert to XYZ
  let x = r * 0.4124 + g * 0.3576 + b * 0.1805;
  let y = r * 0.2126 + g * 0.7152 + b * 0.0722;
  let z = r * 0.0193 + g * 0.1192 + b * 0.9505;

  // Normalize XYZ
  x = x / 0.95047;
  y = y / 1.0;
  z = z / 1.08883;

  // Convert to Lab
  const L = 116 * f(y) - 16;
  const a = 500 * (f(x) - f(y));
  const b_val = 200 * (f(y) - f(z));

  return { L, a, b: b_val };
};

/**
 * Helper function for Lab conversion
 */
const f = (t: number): number => {
  if (t > 0.008856) {
    return Math.pow(t, 1 / 3);
  } else {
    return 7.787 * t + 16 / 116;
  }
};

/**
 * Convert RGB to YUV color space
 */
export const RGBtoYUV = (r: number, g: number, b: number): YUV => {
  const y = 0.299 * r + 0.587 * g + 0.114 * b;
  const u = -0.147 * r - 0.289 * g + 0.436 * b;
  const v = 0.615 * r - 0.515 * g - 0.1 * b;

  return { y, u, v };
};

/**
 * Enhanced skin tone detection using multiple color spaces
 */
export const analyzeSkinTone = (
  r: number,
  g: number,
  b: number
): SkinToneAnalysis => {
  const rgb: RGB = { r, g, b, hex: rgbToHex(r, g, b) };
  const hsv = RGBtoHSV(r, g, b);
  const lab = RGBtoLab(r, g, b);
  const yuv = RGBtoYUV(r, g, b);

  // Enhanced skin tone detection using multiple criteria
  const skinToneResult = detectSkinToneAdvanced(rgb, hsv, lab, yuv);

  return {
    rgb,
    hsv,
    lab,
    yuv,
    ...skinToneResult,
  };
};

/**
 * Advanced skin tone detection using multiple color spaces
 */
const detectSkinToneAdvanced = (
  rgb: RGB,
  hsv: HSV,
  lab: Lab,
  yuv: YUV
): {
  isSkinTone: boolean;
  confidence: number;
  skinToneCategory: string;
  undertone: SeasonUndertone;
} => {
  let confidence = 0;
  let skinToneCategory = 'Unknown';
  let undertone: SeasonUndertone = 'autumn';

  // HSV-based skin detection
  const hsvScore = detectSkinToneHSV(hsv);

  // Lab-based skin detection
  const labScore = detectSkinToneLab(lab);

  // YUV-based skin detection
  const yuvScore = detectSkinToneYUV(yuv);

  // RGB-based skin detection
  const rgbScore = detectSkinToneRGB(rgb);

  // Combine scores for final confidence
  confidence =
    (hsvScore.confidence +
      labScore.confidence +
      yuvScore.confidence +
      rgbScore.confidence) /
    4;

  // Determine if it's a skin tone
  const isSkinTone = confidence > 0.6;

  // Determine skin tone category
  const categories = [
    hsvScore.category,
    labScore.category,
    yuvScore.category,
    rgbScore.category,
  ];
  const categoryCounts: Record<string, number> = {};

  categories.forEach((cat) => {
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  });

  // Get most common category
  let maxCount = 0;
  Object.entries(categoryCounts).forEach(([cat, count]) => {
    if (count > maxCount) {
      maxCount = count;
      skinToneCategory = cat;
    }
  });

  // Determine undertone
  undertone = determineUndertone(hsv, lab, yuv);

  return {
    isSkinTone,
    confidence,
    skinToneCategory,
    undertone,
  };
};

/**
 * HSV-based skin tone detection
 */
const detectSkinToneHSV = (
  hsv: HSV
): { confidence: number; category: string } => {
  let confidence = 0;
  let category = 'Unknown';

  // Enhanced HSV skin detection criteria
  const h = hsv.h;
  const s = hsv.s;
  const v = hsv.v;

  // Skin tone ranges in HSV
  if (s >= 0.05 && s <= 0.9 && v >= 0.15 && v <= 0.95) {
    // Warm skin tones (yellow to red)
    if (h >= 0 && h <= 60) {
      confidence = 0.8;
      if (v > 0.9) category = 'Very Fair';
      else if (v > 0.8) category = 'Fair';
      else if (v > 0.7) category = 'Light';
      else if (v > 0.6) category = 'Medium';
      else if (v > 0.5) category = 'Medium-Tan';
      else if (v > 0.4) category = 'Tan';
      else if (v > 0.3) category = 'Brown';
      else if (v > 0.2) category = 'Dark';
      else category = 'Very Dark';
    }
    // Cool skin tones (pink to purple)
    else if (h >= 300 && h <= 360) {
      confidence = 0.7;
      if (v > 0.9) category = 'Very Fair';
      else if (v > 0.8) category = 'Fair';
      else if (v > 0.7) category = 'Light';
      else if (v > 0.6) category = 'Medium';
      else if (v > 0.5) category = 'Medium-Tan';
      else if (v > 0.4) category = 'Tan';
      else if (v > 0.3) category = 'Brown';
      else if (v > 0.2) category = 'Dark';
      else category = 'Very Dark';
    }
    // Neutral skin tones (olive)
    else if (h >= 60 && h <= 120 && s < 0.4) {
      confidence = 0.75;
      category = 'Olive';
    }
  }

  return { confidence, category };
};

/**
 * Lab-based skin tone detection
 */
const detectSkinToneLab = (
  lab: Lab
): { confidence: number; category: string } => {
  let confidence = 0;
  let category = 'Unknown';

  const L = lab.L;

  // Lab-based skin detection
  if (L >= 20 && L <= 100) {
    confidence = 0.8;

    if (L > 80) category = 'Very Fair';
    else if (L > 70) category = 'Fair';
    else if (L > 60) category = 'Light';
    else if (L > 50) category = 'Medium';
    else if (L > 40) category = 'Medium-Tan';
    else if (L > 30) category = 'Tan';
    else if (L > 20) category = 'Brown';
    else category = 'Dark';
  }

  return { confidence, category };
};

/**
 * YUV-based skin tone detection
 */
const detectSkinToneYUV = (
  yuv: YUV
): { confidence: number; category: string } => {
  let confidence = 0;
  let category = 'Unknown';

  const y = yuv.y;
  const u = yuv.u;
  const v = yuv.v;

  // YUV-based skin detection
  if (y >= 50 && y <= 200 && Math.abs(u) < 50 && Math.abs(v) < 50) {
    confidence = 0.75;

    if (y > 180) category = 'Very Fair';
    else if (y > 160) category = 'Fair';
    else if (y > 140) category = 'Light';
    else if (y > 120) category = 'Medium';
    else if (y > 100) category = 'Medium-Tan';
    else if (y > 80) category = 'Tan';
    else if (y > 60) category = 'Brown';
    else category = 'Dark';
  }

  return { confidence, category };
};

/**
 * RGB-based skin tone detection
 */
const detectSkinToneRGB = (
  rgb: RGB
): { confidence: number; category: string } => {
  let confidence = 0;
  let category = 'Unknown';

  const { r, g, b } = rgb;
  const total = r + g + b;

  // RGB-based skin detection
  if (total > 0) {
    const redRatio = r / total;
    const greenRatio = g / total;
    const blueRatio = b / total;

    // Skin tone typically has higher red and green components
    if (redRatio > 0.25 && greenRatio > 0.25 && blueRatio < 0.4) {
      confidence = 0.8;

      if (r > 220) category = 'Very Fair';
      else if (r > 200) category = 'Fair';
      else if (r > 180) category = 'Light';
      else if (r > 160) category = 'Medium';
      else if (r > 140) category = 'Medium-Tan';
      else if (r > 120) category = 'Tan';
      else if (r > 100) category = 'Brown';
      else if (r > 80) category = 'Dark';
      else category = 'Very Dark';
    }
  }

  return { confidence, category };
};

/**
 * Determine skin undertone based on color analysis
 */
const determineUndertone = (
  hsv: HSV,
  _lab: Lab,
  _yuv: YUV
): SeasonUndertone => {
  return seasonFromSkin(hsv.h, hsv.s, hsv.v);
};

/**
 * Get contrasting text color (black or white) for a background color
 */
export const getContrastTextColor = (hexColor: string): string => {
  const rgb = hexToRgb(hexColor);

  if (!rgb) return '#000000';

  // Calculate luminance using the formula: 0.299*R + 0.587*G + 0.114*B
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;

  // Return black for bright colors, white for dark colors
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

/**
 * Shift hue of a hex color by degrees (0–360). Returns new hex.
 */
export const hexShiftHue = (hex: string, degrees: number): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const hsv = RGBtoHSV(rgb.r, rgb.g, rgb.b);
  const newH = (hsv.h + degrees + 360) % 360;
  const out = HSVtoRGB(newH / 360, hsv.s, hsv.v);
  return out.hex ?? hex;
};

/**
 * Generate a palette of complementary colors
 */
export const generateComplementaryPalette = (
  baseColor: string,
  count: number = 5
): string[] => {
  const rgb = hexToRgb(baseColor);

  if (!rgb) return [];

  const hsv = RGBtoHSV(rgb.r, rgb.g, rgb.b);
  const palette: string[] = [];

  // Add the base color
  palette.push(baseColor);

  // Add complementary colors by shifting the hue
  const hueStep = 360 / count;

  for (let i = 1; i < count; i++) {
    const newHue = (hsv.h + i * hueStep) % 360;
    const newRgb = HSVtoRGB(newHue / 360, hsv.s, hsv.v);
    palette.push(newRgb.hex || '');
  }

  return palette;
};

/**
 * Calculate color similarity between two colors
 */
export const calculateColorSimilarity = (
  color1: string,
  color2: string
): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) return 0;

  // Calculate Euclidean distance in RGB space
  const distance = Math.sqrt(
    Math.pow(rgb1.r - rgb2.r, 2) +
      Math.pow(rgb1.g - rgb2.g, 2) +
      Math.pow(rgb1.b - rgb2.b, 2)
  );

  // Convert to similarity (0-1, where 1 is identical)
  return Math.max(0, 1 - distance / 441.67); // 441.67 is max RGB distance
};

/**
 * Analyze multiple skin tone samples and return the most representative
 */
export const analyzeMultipleSkinTones = (samples: RGB[]): SkinToneAnalysis => {
  if (samples.length === 0) {
    return analyzeSkinTone(128, 128, 128); // Default gray
  }

  // Calculate average RGB values
  const avgR =
    samples.reduce((sum, sample) => sum + sample.r, 0) / samples.length;
  const avgG =
    samples.reduce((sum, sample) => sum + sample.g, 0) / samples.length;
  const avgB =
    samples.reduce((sum, sample) => sum + sample.b, 0) / samples.length;

  return analyzeSkinTone(avgR, avgG, avgB);
};
